import { Component, OnInit, inject } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { AbstractControl, ReactiveFormsModule, ValidationErrors, Validators, FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { EMPTY, forkJoin, Observable, of, tap } from 'rxjs';
import { KENDO_DROPDOWNS } from '@progress/kendo-angular-dropdowns';
import { CategoryResolutionProfile } from 'src/app/common/CategoryResolutionProfile';
import { VatResolutionProfile } from 'src/app/common/VatResolutionProfile';
import { Customer } from 'src/app/common/Customer';
import { ProductCategory } from 'src/app/common/ProductCategory';
import { ScanProviderBinding, ScanProviderBindingPut } from 'src/app/common/ScanProviderBinding';
import { ScanProviderInfo } from 'src/app/common/ScanProviderInfo';
import { Vat } from 'src/app/common/Vat';
import { CustomerService } from 'src/app/services/customer.service';
import { ProductCategoryService } from 'src/app/services/product-category.service';
import { ScanProviderBindingService } from 'src/app/services/scan-provider-binding.service';
import { ScanProviderService } from 'src/app/services/scan-provider.service';
import { VatService } from 'src/app/services/vat.service';
import { CallApiService } from '../../services/api-action.service';
import { AsyncClickDirective } from '../../directives/async-click.directive';
import { FormRowComponent } from '../form-row/form-row.component';
import { FieldErrorDirective } from '../form-row/field-errors.component';

// Passes when the value is empty or parseable JSON. The backend does not
// validate providerConfigJson content (malformed knobs are silently defaulted
// at scan time), so this client-side check is the only guard against typos.
export function jsonValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (!value || !String(value).trim()) {
    return null;
  }
  try {
    JSON.parse(value);
    return null;
  } catch {
    return { invalidJson: true };
  }
}

// Verified example configs per provider key, taken from the backend's
// docs/catalog-sync-scan-providers.md and seed-catalog-sync-bindings.http.
// GET /api/scanproviders exposes no config schema yet, so the hints live here.
export const PROVIDER_CONFIG_EXAMPLES: Record<string, string> = {
  'shopify-json': '{"maxProducts": 25}',
  'woocommerce-store-api': '{"maxProducts": 25}',
  'jsonld-sitemap': '{"maxProducts": 5, "productUrlPattern": "/pu/"}',
  'jsonld-playwright': '{"maxProducts": 25, "productUrlPattern": "/produkt/"}',
  'css-template':
    '{"maxProducts": 25, "listingPath": "catalogue/page-{page}.html", "productLinkSelector": "article.product_pod h3 a[href]", "externalIdPattern": "_(?<id>\\\\d+)/index\\\\.html$", "nameSelector": "article.product_page .product_main h1", "priceSelector": ".product_main .price_color", "descriptionSelector": "#product_description ~ p", "imageSelector": "#product_gallery img[src]", "quantitySelector": ".product_main .availability", "quantityPattern": "\\\\((?<qty>\\\\d+)\\\\s+available\\\\)"}',
  'books-to-scrape': '{"maxProducts": 5}',
  'saleor-demo': '{"maxProducts": 5}',
  'webscraper-scroll': '{"maxProducts": 5}',
};

interface VatItem {
  id: string;
  label: string;
}

// Backend #538: dropdown item for the category-resolution profile picker. key null = the
// house default (the binding sends no key).
interface ProfileItem {
  key: string | null;
  label: string;
}

// Backend #617: dropdown item for the shop's content language. code null = undeclared
// (the binding sends no language; every vocabulary group runs server-side).
export interface ContentLanguageItem {
  code: string | null;
  label: string;
}

/** The scan provider binding form. Its type is inferred from here (#199). */
function buildBindingForm(fb: FormBuilder, binding: ScanProviderBinding) {
  return fb.group({
    customerId: [binding?.customerId, Validators.required],
    providerKey: [binding?.providerKey, Validators.required],
    rootUrl: [
      binding?.rootUrl,
      [Validators.required, Validators.maxLength(2000), Validators.pattern(/^https?:\/\/.+/)],
    ],
    // #633 D: OPTIONAL — „— (keine)" means unresolved products are created deliberately
    // uncategorized (the visible curation backlog). Set it only as declared shop-level
    // knowledge for a homogeneous or breadcrumb-less shop.
    defaultProductCategoryId: [binding?.defaultProductCategoryId ?? null],
    defaultVatId: [binding?.defaultVatId ?? null],
    // #525: optional 3-letter ISO 4217 code (case-insensitive; the API uppercases it).
    expectedCurrency: [binding?.expectedCurrency ?? null, Validators.pattern(/^[A-Za-z]{3}$/)],
    // #538: named category-resolution profile; null = house default (observe-only today).
    categoryResolutionProfileKey: [binding?.categoryResolutionProfileKey ?? null],
    // #579 (slice 4): named VAT-resolution profile; null = house default (shadow).
    vatResolutionProfileKey: [binding?.vatResolutionProfileKey ?? null],
    // #617: the shop's product-text language; null = undeclared.
    contentLanguage: [binding?.contentLanguage ?? null],
    isEnabled: [binding?.isEnabled ?? false],
    cadenceMinutes: [binding?.cadenceMinutes ?? 1440, [Validators.required, Validators.min(1)]],
    providerConfigJson: [
      binding?.providerConfigJson,
      [Validators.maxLength(4000), jsonValidator],
    ],
  });
}
type BindingForm = ReturnType<typeof buildBindingForm>;

@Component({
  imports: [CommonModule, ReactiveFormsModule, KENDO_DROPDOWNS, AsyncClickDirective, FormRowComponent, FieldErrorDirective],
  selector: 'app-scan-provider-edit',
  templateUrl: './scan-provider-edit.component.html',
  styleUrls: ['./scan-provider-edit.component.css'],
})
export class ScanProviderEditComponent implements OnInit {
  fb = inject(FormBuilder);
  private activatedRoute = inject(ActivatedRoute);
  private location = inject(Location);
  private router = inject(Router);
  private bindingService = inject(ScanProviderBindingService);
  private scanProviderService = inject(ScanProviderService);
  private productCategoryService = inject(ProductCategoryService);
  private vatService = inject(VatService);
  private customerService = inject(CustomerService);
  private apiActionService = inject(CallApiService);

  public id: string;
  public bindingForm: BindingForm;
  public providers: ScanProviderInfo[] = [];
  // #633 D: the explicit "no default" choice — null round-trips as an absent default.
  // #178: the dropdown renders `name` now — see search-bar.
  public readonly noDefaultCategoryItem = {
    id: null as string | null,
    shortName: $localize`— (keine)`,
    name: $localize`— (keine)`,
  };

  public productCategories: ProductCategory[] = [];
  public vatItems: VatItem[] = [];
  public customers: Customer[] = [];
  public filteredCustomers: Customer[] = [];
  public profileItems: ProfileItem[] = [];
  public defaultProfileItem: ProfileItem = { key: null, label: $localize`Systemstandard` };

  // Backend #617: the two languages the resolution vocabulary carries (plus the neutral
  // group, which is always in scope). Deliberately a static list — the backend validates
  // only the ISO 639-1 shape, and offering a language without a vocabulary group would
  // scope resolution to the neutral terms alone.
  public readonly contentLanguageItems: ContentLanguageItem[] = [
    { code: 'de', label: $localize`Deutsch` },
    { code: 'en', label: $localize`Englisch` },
  ];
  public readonly undeclaredLanguageItem: ContentLanguageItem = {
    code: null,
    label: $localize`Nicht festgelegt (alle Sprachen)`,
  };

  // Backend #579 (slice 4): the VAT-resolution profile dropdown — the mirror of the category
  // one. The empty choice names the house default; 'live' is offered but refused server-side
  // until the rulings and the AI evaluation are signed off (the hint says so).
  public vatProfileItems: { key: string; label: string }[] = [];
  public defaultVatProfileItem: { key: string | null; label: string } = {
    key: null,
    label: $localize`Systemstandard`,
  };

  get f() {
    return this.bindingForm.controls;
  }

  public get configExample(): string {
    return PROVIDER_CONFIG_EXAMPLES[this.bindingForm?.controls.providerKey?.value] ?? null;
  }

  public ngOnInit() {
    this.id = this.activatedRoute.snapshot.paramMap.get('id');

    this.apiActionService.callWithToasts(
      forkJoin({
        providers: this.scanProviderService.get(),
        categories: this.productCategoryService.get(),
        vats: this.vatService.get(),
        customers: this.customerService.getAllForLookup(),
        profiles: this.bindingService.getResolutionProfiles(),
        vatProfiles: this.bindingService.getVatResolutionProfiles(),
        binding: this.id ? this.bindingService.getById(this.id) : of(null as ScanProviderBinding),
      }),
      null,
      $localize`Beim Laden der Daten ist ein Fehler aufgetreten.`,
      ({ providers, categories, vats, customers, profiles, vatProfiles, binding }) => {
        this.providers = providers;
        this.productCategories = categories;
        this.vatItems = vats.map((v: Vat) => ({ id: v.id, label: v.percentage + ' %' }));
        this.customers = customers;
        this.filteredCustomers = customers;
        this.profileItems = profiles.map((p) => ({ key: p.key, label: this.profileLabel(p) }));
        // The empty choice names what it currently resolves to, so the operator sees the
        // effective behaviour instead of a bare "default".
        const houseDefault = profiles.find((p) => p.isDefault);
        if (houseDefault) {
          this.defaultProfileItem = {
            key: null,
            label: $localize`Systemstandard` + ` (${this.profileLabel(houseDefault)})`,
          };
        }
        // Backend #579 (slice 4): same shape for the VAT profile registry.
        this.vatProfileItems = vatProfiles.map((p) => ({ key: p.key, label: this.vatProfileLabel(p) }));
        const vatHouseDefault = vatProfiles.find((p) => p.isDefault);
        if (vatHouseDefault) {
          this.defaultVatProfileItem = {
            key: null,
            label: $localize`Systemstandard` + ` (${this.vatProfileLabel(vatHouseDefault)})`,
          };
        }
        this.bindingForm = this.createForm(binding);
        if (this.id) {
          // Customer and provider of a binding are immutable (the PUT model
          // carries neither — backend #506 C4: provider replacement = delete +
          // recreate); both pickers are only active when creating.
          this.bindingForm.controls.customerId.disable();
          this.bindingForm.controls.providerKey.disable();
        }
      }
    );
  }

  public onCustomerFilterChange(value: string) {
    const search = (value ?? '').toLowerCase();
    this.filteredCustomers = this.customers.filter((c) =>
      (c.fullName ?? '').toLowerCase().includes(search)
    );
  }

  public applyConfigExample() {
    const control = this.bindingForm.controls.providerConfigJson;
    control.setValue(this.configExample);
    control.markAsDirty();
  }

  // Backend #538: 'Observe' records only (listings keep the default category), 'Live'
  // applies the chain's confident answer at listing create.
  private profileLabel(profile: CategoryResolutionProfile): string {
    const mode =
      profile.mode === 'Live'
        ? $localize`aktiv – Kategorien werden übernommen`
        : $localize`beobachtend`;
    return `${profile.key} (${mode})`;
  }

  // Backend #579 (slice 4): 'Observe' records only, 'Suggest' stores the verdict on the
  // listing for the seller's decision, 'Live' applies it under the C rule — and is refused
  // server-side until the rulings and the AI evaluation are signed off.
  public vatProfileLabel(profile: VatResolutionProfile): string {
    let mode: string;
    switch (profile.mode) {
      case 'Live':
        mode = $localize`aktiv – MwSt.-Satz wird übernommen (nur nach Freigabe)`;
        break;
      case 'Suggest':
        mode = $localize`Vorschlag – der Anbieter entscheidet`;
        break;
      default:
        mode = $localize`beobachtend`;
    }
    return `${profile.key} (${mode})`;
  }

  public submitForm(): Observable<unknown> {
    if (!this.bindingForm.valid) {
      return EMPTY;
    }

    const value = this.bindingForm.getRawValue();
    const putModel: ScanProviderBindingPut = {
      rootUrl: value.rootUrl,
      defaultProductCategoryId: value.defaultProductCategoryId,
      defaultVatId: value.defaultVatId ?? null,
      isEnabled: value.isEnabled ?? false,
      cadenceMinutes: value.cadenceMinutes,
      providerConfigJson: value.providerConfigJson?.trim() ? value.providerConfigJson.trim() : null,
      // #525: empty = fall back to the seller's defaultCurrency → EUR. Stored uppercase
      // server-side; scraped currencies are only verified against this, never trusted.
      expectedCurrency: value.expectedCurrency?.trim() ? value.expectedCurrency.trim() : null,
      // #538: null = house default. The key is registry-validated server-side (400 on typos),
      // but the dropdown only offers registry values anyway.
      categoryResolutionProfileKey: value.categoryResolutionProfileKey ?? null,
      // #579 (slice 4): the VAT mirror — null = house default (shadow).
      vatResolutionProfileKey: value.vatResolutionProfileKey ?? null,
      // #617: null = undeclared — every vocabulary language group runs; homograph ties
      // escalate as ambiguous instead of resolving against the wrong language.
      contentLanguage: value.contentLanguage ?? null,
    };

    const action$ = this.id
      ? this.apiActionService.callWithToasts$(
          this.bindingService.update(this.id, putModel),
          $localize`Scan-Provider-Zuordnung wurde aktualisiert.`,
          $localize`Beim Aktualisieren der Scan-Provider-Zuordnung ist ein Fehler aufgetreten.`
        )
      : this.apiActionService.callWithToasts$(
          this.bindingService.create({
            ...putModel,
            customerId: value.customerId,
            providerKey: value.providerKey,
          }),
          $localize`Scan-Provider-Zuordnung wurde angelegt.`,
          $localize`Beim Anlegen der Scan-Provider-Zuordnung ist ein Fehler aufgetreten.`
        );

    // Forward-navigate instead of history.back(): Firefox restores the prior
    // page via bfcache on history.back(), which discards the success toast.
    return action$.pipe(tap(() => this.router.navigateByUrl('/scan-provider-list')));
  }

  public onBackClicked() {
    this.location.back();
  }

  private createForm(binding: ScanProviderBinding): BindingForm {
    return buildBindingForm(this.fb, binding);
  }
}
